// src/components/rewards/RewardAnimation.js
import React, { useEffect, useState, useRef, useCallback } from 'react';

const RewardAnimation = ({ type, duration = 5, name, onComplete, isPreview = false, audioURL = null, language = 'mr' }) => {
  const [showContinue, setShowContinue] = useState(false);
  const [particles, setParticles] = useState([]);
  const timerRef = useRef(null);
  const intervalRef = useRef(null);
  const audioRef = useRef(null);

  const congratsText = language === 'mr'
    ? `${name}, आजची सर्व कामे पूर्ण केलीस! 🎉`
    : `${name}, you finished all tasks today! 🎉`;

  const continueText = language === 'mr' ? 'पुढे जा' : 'Continue';

  const playAudio = useCallback(() => {
    if (!audioURL) return;
    try {
      const audio = new Audio(audioURL);
      audio.volume = 0.8;
      audioRef.current = audio;
      audio.play().catch(() => {});
    } catch (e) {}
  }, [audioURL]);

  const generateFireworkBurst = useCallback(() => {
    const cx = 10 + Math.random() * 80;
    const cy = 10 + Math.random() * 60;
    const color = ['#ff6b6b','#ffd93d','#6bcb77','#4d96ff','#ff6bff','#ff9f43','#fff'][Math.floor(Math.random()*7)];
    const burst = [];
    for (let i = 0; i < 16; i++) {
      burst.push({
        id: `${Date.now()}-${i}-${Math.random()}`,
        cx, cy, angle: (i / 16) * 360, color,
        size: 5 + Math.random() * 5
      });
    }
    return burst;
  }, []);

  const generateConfettiBatch = useCallback(() => {
    const batch = [];
    for (let i = 0; i < 20; i++) {
      batch.push({
        id: `${Date.now()}-${i}-${Math.random()}`,
        x: Math.random() * 100,
        delay: Math.random() * 0.5,
        fallDuration: 2 + Math.random() * 2,
        color: ['#ff6b6b','#ffd93d','#6bcb77','#4d96ff','#ff6bff','#ff9f43','#fff'][Math.floor(Math.random()*7)],
        size: 6 + Math.random() * 8,
        rotation: Math.random() * 360,
        shape: Math.random() > 0.5 ? 'rect' : 'circle'
      });
    }
    return batch;
  }, []);

  useEffect(() => {
    playAudio();

    if (type === 'fireworks') {
      const addBursts = () => {
        setParticles(prev => {
          const newBursts = [];
          for (let i = 0; i < 3; i++) newBursts.push(...generateFireworkBurst());
          return [...prev, ...newBursts].slice(-200);
        });
      };
      addBursts();
      intervalRef.current = setInterval(addBursts, 600);
    } else if (type === 'confetti') {
      const addBatch = () => {
        setParticles(prev => [...prev, ...generateConfettiBatch()].slice(-300));
      };
      addBatch();
      intervalRef.current = setInterval(addBatch, 400);
    }

    const timer = setTimeout(() => {
      clearInterval(intervalRef.current);
      if (isPreview) {
        if (onComplete) onComplete();
      } else {
        setShowContinue(true);
      }
    }, duration * 1000);
    timerRef.current = timer;

    return () => {
      clearTimeout(timer);
      clearInterval(intervalRef.current);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [type, duration, isPreview, onComplete, playAudio, generateFireworkBurst, generateConfettiBatch]);

  const overlayStyle = {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.93)',
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    zIndex: 2000, overflow: 'hidden'
  };

  const textWrapStyle = {
    position: 'relative', zIndex: 10,
    textAlign: 'center', padding: '0 24px',
    pointerEvents: 'none'
  };

  const nameStyle = {
    color: 'white',
    fontSize: 'clamp(20px, 5vw, 34px)',
    fontWeight: 800,
    textShadow: '0 2px 16px rgba(0,0,0,0.9)',
    lineHeight: 1.4
  };

  const continueBtn = {
    marginTop: '32px', background: '#007fff', color: 'white',
    border: 'none', padding: 'clamp(12px,3vw,16px) clamp(28px,6vw,48px)',
    borderRadius: '14px', fontSize: 'clamp(15px,3vw,18px)',
    fontWeight: 700, cursor: 'pointer', zIndex: 10, position: 'relative'
  };

  if (type === 'fireworks') {
    return (
      <div style={overlayStyle}>
        <style>{`
          @keyframes fwParticle {
            0% { transform: translate(0,0) scale(1); opacity: 1; }
            100% { transform: translate(var(--tx), var(--ty)) scale(0); opacity: 0; }
          }
        `}</style>
        {particles.map(p => (
          <div key={p.id} style={{
            position: 'absolute',
            left: `${p.cx}%`, top: `${p.cy}%`,
            width: `${p.size}px`, height: `${p.size}px`,
            borderRadius: '50%', background: p.color,
            '--tx': `${Math.cos(p.angle * Math.PI / 180) * (60 + Math.random() * 60)}px`,
            '--ty': `${Math.sin(p.angle * Math.PI / 180) * (60 + Math.random() * 60)}px`,
            animation: `fwParticle 1s ease-out both`,
            zIndex: 5, pointerEvents: 'none'
          }} />
        ))}
        <div style={textWrapStyle}>
          <div style={nameStyle}>{congratsText}</div>
        </div>
        {showContinue && (
          <button style={continueBtn} onClick={onComplete}>{continueText}</button>
        )}
      </div>
    );
  }

  if (type === 'confetti') {
    return (
      <div style={overlayStyle}>
        <style>{`
          @keyframes cFall {
            0% { transform: translateY(-30px) rotate(0deg); opacity: 1; }
            100% { transform: translateY(110vh) rotate(var(--rot)); opacity: 0.7; }
          }
        `}</style>
        {particles.map(p => (
          <div key={p.id} style={{
            position: 'absolute',
            left: `${p.x}%`, top: '-30px',
            width: p.shape === 'circle' ? `${p.size}px` : `${p.size * 1.5}px`,
            height: `${p.size}px`,
            borderRadius: p.shape === 'circle' ? '50%' : '2px',
            background: p.color,
            '--rot': `${p.rotation + 360}deg`,
            animation: `cFall ${p.fallDuration}s linear ${p.delay}s both`,
            zIndex: 5, pointerEvents: 'none'
          }} />
        ))}
        <div style={textWrapStyle}>
          <div style={nameStyle}>{congratsText}</div>
        </div>
        {showContinue && (
          <button style={continueBtn} onClick={onComplete}>{continueText}</button>
        )}
      </div>
    );
  }

  return null;
};

export default RewardAnimation;